import React, { useState, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import "./Sidebar.css";
import { useNavigate, useLocation } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import {
  FaTachometerAlt,
  FaUserCircle,
  FaListUl,
  FaBoxes,
  FaChartLine,
  FaCashRegister,
  FaUsers,
  FaAngleDown,
  FaAngleUp,
  FaLock,
  FaCreditCard,
  FaDownload,
  FaSync,
  FaWifi,
  FaExclamationTriangle,
  FaCog,
  FaPlug,
  FaCheckCircle,
  FaDatabase,
  FaArrowUp,
  FaArrowDown,
  FaClock,
  FaBolt,
  FaSearch,
  FaHdd,
} from "react-icons/fa";
import api from "../../api";

const ENV = process.env.REACT_APP_ENV || "production";
const IS_DESKTOP = ENV === "desktop";

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [userType, setUserType] = useState(null);
  const [staffPermissions, setStaffPermissions] = useState([]);
  const [hasInventoryAccess, setHasInventoryAccess] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const [subscriptionStatus, setSubscriptionStatus] = useState(null);

  // ── Sync state ──
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncProgress, setSyncProgress] = useState(null);
  const [pullProgress, setPullProgress] = useState(null);
  const [progressOpen, setProgressOpen] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncMessage, setLastSyncMessage] = useState(null);
  const [lastSyncTime, setLastSyncTime] = useState(null);

  // ── Connector / RetailMan state ──
  const [connectorSettings, setConnectorSettings] = useState(null);
  const [togglingRetailman, setTogglingRetailman] = useState(false);
  const [retailmanProgress, setRetailmanProgress] = useState(null);
  const [retailmanSyncing, setRetailmanSyncing] = useState(false);
  const [rmanSyncDetail, setRmanSyncDetail] = useState(null);
  const [diagnosing, setDiagnosing] = useState(false);
  const [diagnoseResult, setDiagnoseResult] = useState(null);
  const [diagnoseOpen, setDiagnoseOpen] = useState(false);

  // ── Modals ──
  const [confirmDialog, setConfirmDialog] = useState({ visible: false, title: "", message: "", onConfirm: null });
  const [alertDialog, setAlertDialog] = useState({ visible: false, title: "", message: "", onClose: null });

  const showConfirm = (title, message) =>
    new Promise((resolve) => {
      setConfirmDialog({
        visible: true, title, message,
        onConfirm: (result) => {
          setConfirmDialog({ visible: false, title: "", message: "", onConfirm: null });
          resolve(result);
        },
      });
    });

  const showAlert = (title, message) =>
    new Promise((resolve) => {
      setAlertDialog({
        visible: true, title, message,
        onClose: () => {
          setAlertDialog({ visible: false, title: "", message: "", onClose: null });
          resolve();
        },
      });
    });

  // ── Decode token ──
  useEffect(() => {
    const token = localStorage.getItem("access_token");
    if (!token) return;
    try {
      const decoded = jwtDecode(token);
      setUserType((decoded.user_type?.value || decoded.user_type || "").toString().trim());
    } catch { setUserType(null); }
  }, []);

  // ── Staff permissions ──
  useEffect(() => {
    const fetch = async () => {
      try {
        if (userType === "PHARMACY" || userType === "PHARMACYBRANCH") { setHasInventoryAccess(true); return; }
        if (userType === "PHARMACYSTAFF") {
          const res = await api.get("/staffmanager/my-permissions");
          const perms = res.data || [];
          setStaffPermissions(perms);
          setHasInventoryAccess(perms.some(p => ["INVENTORY_MANAGE","INVENTORY_MANAGEMENT"].includes(p.toUpperCase())));
        }
      } catch { setHasInventoryAccess(false); }
    };
    if (userType) fetch();
  }, [userType]);

  // ── Subscription status ──
  useEffect(() => {
    if (userType !== "PHARMACY") return;
    const fetch = async () => {
      try {
        const res = await api.get("/subscriptions/my/subscription");
        let s = null;
        if (res.data.active?.status) s = res.data.active;
        else if (res.data.trialing?.status) s = res.data.trialing;
        else if (res.data.id && ["ACTIVE","TRIALING"].includes(res.data.status)) s = res.data;
        setSubscriptionStatus(s);
      } catch { if (!IS_DESKTOP) setSubscriptionStatus(null); }
    };
    fetch();
  }, [userType]);

  // ── Sync status — polls every 30s ──
  const fetchSyncStatus = useCallback(async () => {
    if (!IS_DESKTOP) return;
    try { const res = await api.get("/sync/status"); setSyncStatus(res.data); }
    catch { setSyncStatus(null); }
  }, []);

  useEffect(() => {
    if (!IS_DESKTOP) return;
    fetchSyncStatus();
    const i = setInterval(fetchSyncStatus, 30000);
    return () => clearInterval(i);
  }, [fetchSyncStatus]);

  // ── Sync progress — polls every 5s when panel open ──
  const fetchSyncProgress = useCallback(async () => {
    if (!IS_DESKTOP) return;
    try { const res = await api.get("/sync/progress"); setSyncProgress(res.data); }
    catch {}
  }, []);

  useEffect(() => {
    if (!IS_DESKTOP || !progressOpen) return;
    fetchSyncProgress();
    const i = setInterval(fetchSyncProgress, 5000);
    return () => clearInterval(i);
  }, [fetchSyncProgress, progressOpen]);

  // ── Pull progress — polls every 2s ──
  const fetchPullProgress = useCallback(async () => {
    if (!IS_DESKTOP) return;
    try { const res = await api.get("/sync/pull-progress"); setPullProgress(res.data); }
    catch {}
  }, []);

  useEffect(() => {
    if (!IS_DESKTOP) return;
    const i = setInterval(fetchPullProgress, 2000);
    return () => clearInterval(i);
  }, [fetchPullProgress]);

  // ── Connector settings ──
  const fetchConnectorSettings = useCallback(async () => {
    if (!IS_DESKTOP) return;
    try { const res = await api.get("/connectors/settings"); setConnectorSettings(res.data); }
    catch {}
  }, []);

  useEffect(() => {
    if (!IS_DESKTOP) return;
    fetchConnectorSettings();
  }, [fetchConnectorSettings]);

  // ── RetailMan progress — polls every 3s when settings open ──
  const fetchRetailmanProgress = useCallback(async () => {
    if (!IS_DESKTOP) return;
    try { const res = await api.get("/connectors/retailman/progress"); setRetailmanProgress(res.data); }
    catch {}
  }, []);

  useEffect(() => {
    if (!IS_DESKTOP || !settingsOpen) return;
    fetchRetailmanProgress();
    const i = setInterval(fetchRetailmanProgress, 3000);
    return () => clearInterval(i);
  }, [fetchRetailmanProgress, settingsOpen]);

  // ── RetailMan real-time sync progress — polls every 1s during active sync ──
  const fetchRmanSyncProgress = useCallback(async () => {
    if (!IS_DESKTOP) return;
    try { const res = await api.get("/connectors/retailman/sync-progress"); setRmanSyncDetail(res.data); }
    catch {}
  }, []);

  useEffect(() => {
    if (!IS_DESKTOP || !settingsOpen) return;
    fetchRmanSyncProgress();
    const i = setInterval(fetchRmanSyncProgress, 1000);
    return () => clearInterval(i);
  }, [fetchRmanSyncProgress, settingsOpen]);

  // ── Toggle RetailMan ──
  const handleToggleRetailman = async () => {
    if (togglingRetailman) return;
    if (!connectorSettings) { await fetchConnectorSettings(); return; }
    const isEnabled = connectorSettings?.retailman?.enabled;
    if (!isEnabled && !connectorSettings?.retailman?.detected) {
      await showAlert("RetailMan Not Detected", "RetailMan does not appear to be installed.\n\nMyApoti checks:\n• C:\\Rman\n• C:\\RetailMan\n• C:\\Program Files\\Rman");
      return;
    }
    const confirmed = await showConfirm(
      isEnabled ? "Disable RetailMan Sync" : "Enable RetailMan Sync",
      isEnabled
        ? "RetailMan will stop syncing.\n\nExisting inventory remains — only future auto-syncs stop."
        : `Detected at:\n${connectorSettings?.retailman?.folder}\n\nMyApoti will sync RetailMan stock every 10 minutes.`
    );
    if (!confirmed) return;
    setTogglingRetailman(true);
    try {
      const res = await api.post("/connectors/retailman/toggle");
      setConnectorSettings(prev => ({ ...prev, retailman: { ...prev?.retailman, enabled: res.data.retailman_enabled } }));
      await showAlert(res.data.retailman_enabled ? "✅ RetailMan Sync Enabled" : "RetailMan Sync Disabled", res.data.message);
    } catch (err) {
      await showAlert("Error", err.response?.data?.detail || "Could not update RetailMan settings.");
    } finally {
      setTogglingRetailman(false);
      fetchConnectorSettings();
    }
  };

  // ── RetailMan sync now ──
  const handleRetailmanSyncNow = async () => {
    if (retailmanSyncing || rman?.is_syncing) return;
    setRetailmanSyncing(true);
    try {
      const res = await api.post("/connectors/retailman/sync-now");
      if (res.data.status === "already_running") {
        await showAlert("Already Running", "RetailMan sync is already in progress.");
      } else if (res.data.status === "skipped") {
        await showAlert("No Changes", "No changes detected in RetailMan data — nothing to sync.");
      }
    } catch (err) {
      if (err.response?.status === 425) {
        await showAlert(
          "⏳ Initial Sync Pending",
          "MyApoti needs to complete the first cloud sync before RetailMan can run.\n\nWait for the sync bar to show 'All changes synced to cloud' then try again."
        );
      } else {
        await showAlert("Error", err.response?.data?.detail || "Could not start RetailMan sync.");
      }
    } finally {
      setRetailmanSyncing(false);
    }
  };

  // ── RetailMan diagnose ──
  const handleRetailmanDiagnose = async () => {
    if (diagnosing) return;
    setDiagnosing(true);
    setDiagnoseResult(null);
    try {
      const res = await api.get("/connectors/retailman/diagnose");
      setDiagnoseResult(res.data);
      setDiagnoseOpen(true);
    } catch (err) {
      await showAlert("Diagnose Error", err.response?.data?.detail || "Could not run diagnostics.");
    } finally {
      setDiagnosing(false);
    }
  };

  // ── Manual cloud sync ──
  const handleManualSync = async () => {
    if (isSyncing) return;
    setIsSyncing(true);
    setLastSyncMessage(null);
    try {
      const res = await api.post("/sync/now");
      const { synced, pulled, failed, pull_errors } = res.data;
      const total = (synced || 0) + (pulled || 0);
      const errors = (failed || 0) + (pull_errors || 0);
      setLastSyncMessage(errors > 0
        ? { type: "warning", text: `Synced ${total} record(s) — ${errors} error(s).` }
        : { type: "success", text: `Sync complete — ${total} record(s) updated.` }
      );
      setLastSyncTime(new Date());
      await fetchSyncStatus();
      if (progressOpen) await fetchSyncProgress();
    } catch (err) {
      setLastSyncMessage({ type: "error", text: err.response?.data?.detail || "Sync failed. Check your connection." });
    } finally {
      setIsSyncing(false);
      setTimeout(() => setLastSyncMessage(null), 5000);
    }
  };

  // ── Analytics ──
  useEffect(() => {
    if (["/Analytics","/DrugAnalytics","/BranchAnalytics"].some(p => location.pathname.startsWith(p))) setAnalyticsOpen(true);
  }, [location.pathname]);

  const handleAnalyticsClick = () => { navigate("/Analytics"); setAnalyticsOpen(true); };

  const handleInventoryClick = async () => {
    if (userType === "PHARMACYSTAFF" && !hasInventoryAccess) {
      await showAlert("🔒 Access Denied", "You don't have permission to access Inventory Management.\n\nRequired Permission: INVENTORY_MANAGE");
      return;
    }
    navigate("/ManageInventory");
  };

  const handleExportInventory = async () => {
    const confirmed = await showConfirm("📊 Export Inventory",
      isPharmacyOwner ? "Export full inventory as Excel file?" : "Export branch inventory as Excel file?");
    if (!confirmed) return;
    if (isPharmacyOwner) {
      if (!subscriptionStatus) { await showAlert("❌ Subscription Required", "Export requires an active subscription."); navigate("/subscription"); return; }
      if (!["ACTIVE","TRIALING"].includes(subscriptionStatus.status)) { await showAlert("❌ Subscription Inactive", "Please renew your subscription."); navigate("/subscription"); return; }
      if (subscriptionStatus.tier === "FREE") { await showAlert("❌ Upgrade Required", "Export is not available on FREE. Upgrade to BASIC or higher."); navigate("/subscription"); return; }
    }
    try {
      setIsExporting(true);
      const response = await api.get("/POS/pos/inventories/export-excel", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const cd = response.headers["content-disposition"];
      link.setAttribute("download", cd ? cd.split("filename=")[1]?.replace(/"/g,"") : `inventory_${new Date().toISOString().split("T")[0]}.xlsx`);
      document.body.appendChild(link); link.click(); link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      await showAlert("✅ Export Successful", "Inventory exported successfully!");
    } catch (err) {
      if (err.response?.status === 403) { await showAlert("❌ Export Denied", err.response?.data?.detail || "Export requires an active paid subscription."); if (isPharmacyOwner) navigate("/subscription"); }
      else if (err.response?.status === 404) await showAlert("❌ No Inventory", "No inventory found to export.");
      else await showAlert("❌ Export Failed", err.response?.data?.detail || err.message || "Export failed.");
    } finally { setIsExporting(false); }
  };

  // ── Derived ──
  const isStaff = userType === "PHARMACYSTAFF";
  const isBranchUser = userType === "PHARMACYBRANCH";
  const isPharmacyOwner = userType === "PHARMACY";
  const ownerCanExport = isPharmacyOwner && subscriptionStatus && ["ACTIVE","TRIALING"].includes(subscriptionStatus.status) && subscriptionStatus.tier !== "FREE";
  const retailman = connectorSettings?.retailman;
  const rman = retailmanProgress;

  // ── Formatters ──
  const formatSyncTime = (date) => {
    if (!date) return null;
    const d = Math.floor((new Date() - date) / 60000);
    return d < 1 ? "just now" : d === 1 ? "1 min ago" : `${d} mins ago`;
  };

  const formatLastSynced = (iso) => {
    if (!iso) return "Never";
    try {
      const d = new Date(iso);
      const mins = Math.floor((Date.now() - d.getTime()) / 60000);
      if (mins < 1) return "just now";
      if (mins < 60) return `${mins}m ago`;
      const hrs = Math.floor(mins / 60);
      if (hrs < 24) return `${hrs}h ago`;
      return d.toLocaleDateString();
    } catch { return "Unknown"; }
  };

  const formatNextSync = (s) => {
    if (!s) return null;
    return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m`;
  };

  // ── RetailMan progress bar calc ──
  const rmanSyncProgress = rmanSyncDetail || rman?.sync_progress || {};
  const rmanTotal     = rmanSyncDetail?.total     || rman?.sync_progress?.total     || 0;
  const rmanProcessed = rmanSyncDetail?.processed || rman?.sync_progress?.processed || 0;
  const rmanAdded     = rmanSyncDetail?.added     || 0;
  const rmanUpdated   = rmanSyncDetail?.updated   || 0;
  const rmanSkipped   = rmanSyncDetail?.skipped   || 0;
  const rmanFailed    = rmanSyncDetail?.failed    || 0;
  const rmanPercent   = rmanSyncDetail?.percent != null
    ? rmanSyncDetail.percent
    : rmanTotal > 0 ? Math.min(99, Math.round((rmanProcessed / rmanTotal) * 100)) : null;
  const isRmanSyncing = rmanSyncDetail?.is_syncing || rman?.is_syncing || rman?.sync_progress?.is_syncing;

  // ── Pull progress bar calc ──
  const isPulling = pullProgress?.is_pulling;
  const pullTotal = pullProgress?.total || 0;
  const pullPulled = pullProgress?.pulled || 0;
  const pullPercent = pullTotal > 0 ? Math.min(99, Math.round((pullPulled / pullTotal) * 100)) : null;

  // ── Modals JSX ──
  const modals = (
    <>
      {alertDialog.visible && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:99999 }}>
          <div style={{ background:"#fff", borderRadius:"10px", padding:"2rem", maxWidth:"460px", width:"90%", boxShadow:"0 8px 32px rgba(0,0,0,0.2)" }}>
            <h3 style={{ margin:"0 0 1rem", color:"#333", fontSize:"1.1rem" }}>{alertDialog.title}</h3>
            <p style={{ color:"#555", fontSize:"0.95rem", lineHeight:1.6, margin:"0 0 1.5rem", whiteSpace:"pre-line" }}>{alertDialog.message}</p>
            <div style={{ display:"flex", justifyContent:"flex-end" }}>
              <button onClick={alertDialog.onClose} style={{ padding:"0.6rem 1.5rem", background:"#007bff", color:"white", border:"none", borderRadius:"6px", cursor:"pointer", fontWeight:600 }}>OK</button>
            </div>
          </div>
        </div>
      )}
      {confirmDialog.visible && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:99999 }}>
          <div style={{ background:"#fff", borderRadius:"10px", padding:"2rem", maxWidth:"460px", width:"90%", boxShadow:"0 8px 32px rgba(0,0,0,0.2)" }}>
            <h3 style={{ margin:"0 0 1rem", color:"#333", fontSize:"1.1rem" }}>{confirmDialog.title}</h3>
            <p style={{ color:"#555", fontSize:"0.95rem", lineHeight:1.6, margin:"0 0 1.5rem", whiteSpace:"pre-line" }}>{confirmDialog.message}</p>
            <div style={{ display:"flex", gap:"1rem", justifyContent:"flex-end" }}>
              <button onClick={() => confirmDialog.onConfirm(false)} style={{ padding:"0.6rem 1.25rem", background:"#f8f9fa", color:"#333", border:"1px solid #ddd", borderRadius:"6px", cursor:"pointer", fontWeight:500 }}>Cancel</button>
              <button onClick={() => confirmDialog.onConfirm(true)} style={{ padding:"0.6rem 1.25rem", background:"#007bff", color:"white", border:"none", borderRadius:"6px", cursor:"pointer", fontWeight:600 }}>Confirm</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Diagnose Result Modal ── */}
      {diagnoseOpen && diagnoseResult && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:99999 }}>
          <div style={{ background:"#fff", borderRadius:"10px", padding:"1.5rem", maxWidth:"520px", width:"95%", boxShadow:"0 8px 32px rgba(0,0,0,0.2)", maxHeight:"80vh", overflowY:"auto" }}>
            <h3 style={{ margin:"0 0 1rem", color:"#333", fontSize:"1.1rem", display:"flex", alignItems:"center", gap:"8px" }}>
              🔍 RetailMan Diagnostics
            </h3>

            {!diagnoseResult.detected ? (
              <div style={{ color:"#dc2626", fontSize:"0.9rem", marginBottom:"1rem" }}>
                ❌ RetailMan folder not found on this machine.
              </div>
            ) : (
              <>
                <div style={{ marginBottom:"12px" }}>
                  <div style={{ fontSize:"0.75rem", color:"#9ca3af", marginBottom:"2px" }}>FOLDER</div>
                  <div style={{ fontSize:"0.85rem", fontFamily:"monospace", color:"#374151", background:"#f9fafb", padding:"6px 8px", borderRadius:"6px", border:"1px solid #e5e7eb" }}>
                    {diagnoseResult.folder}
                  </div>
                </div>

                <div style={{ marginBottom:"12px" }}>
                  <div style={{ fontSize:"0.75rem", color:"#9ca3af", marginBottom:"4px" }}>DISK SPACE</div>
                  <div style={{ display:"flex", gap:"12px", flexWrap:"wrap" }}>
                    <span style={{ fontSize:"0.82rem", color: diagnoseResult.disk.free_gb < 2 ? "#dc2626" : "#16a34a", fontWeight:600 }}>
                      {diagnoseResult.disk.free_gb} GB free
                    </span>
                    <span style={{ fontSize:"0.82rem", color:"#6b7280" }}>
                      {diagnoseResult.disk.used_gb} GB used of {diagnoseResult.disk.total_gb} GB ({diagnoseResult.disk.percent_used}%)
                    </span>
                  </div>
                  {diagnoseResult.disk.free_gb < 2 && (
                    <div style={{ fontSize:"0.78rem", color:"#dc2626", marginTop:"4px" }}>
                      ⚠️ Low disk space — this may prevent RetailMan from saving records.
                    </div>
                  )}
                </div>

                {diagnoseResult.likely_stock_file && (
                  <div style={{ marginBottom:"12px", padding:"8px 10px", background:"rgba(59,130,246,0.05)", borderRadius:"8px", border:"1px solid rgba(59,130,246,0.2)" }}>
                    <div style={{ fontSize:"0.75rem", color:"#9ca3af", marginBottom:"4px" }}>LIKELY STOCK FILE</div>
                    <div style={{ fontSize:"0.85rem", fontWeight:600, color:"#1d4ed8" }}>
                      📄 {diagnoseResult.likely_stock_file.name}
                    </div>
                    <div style={{ fontSize:"0.78rem", color:"#6b7280", marginTop:"2px" }}>
                      {diagnoseResult.likely_stock_file.size_kb} KB · Last modified: {new Date(diagnoseResult.likely_stock_file.last_modified).toLocaleString()}
                    </div>
                    <div style={{ fontSize:"0.78rem", marginTop:"2px" }}>
                      Readable: {diagnoseResult.likely_stock_file.readable ? "✅" : "❌"} · Writable: {diagnoseResult.likely_stock_file.writable ? "✅" : "❌"}
                    </div>
                  </div>
                )}

                <div style={{ marginBottom:"12px" }}>
                  <div style={{ fontSize:"0.75rem", color:"#9ca3af", marginBottom:"6px" }}>
                    ALL DBF FILES ({diagnoseResult.dbf_files?.length || 0})
                  </div>
                  {diagnoseResult.dbf_files?.map((f, i) => (
                    <div key={i} style={{ padding:"8px 10px", marginBottom:"6px", background:"#f9fafb", borderRadius:"6px", border:"1px solid #e5e7eb", fontSize:"0.80rem" }}>
                      <div style={{ fontWeight:600, color:"#374151", marginBottom:"2px" }}>📄 {f.name}</div>
                      <div style={{ color:"#6b7280" }}>{f.size_kb} KB · Modified: {new Date(f.last_modified).toLocaleString()}</div>
                      <div style={{ color:"#6b7280", marginTop:"2px" }}>Read: {f.readable ? "✅" : "❌"} · Write: {f.writable ? "✅" : "❌"}</div>
                    </div>
                  ))}
                </div>

                {diagnoseResult.dbf_files?.every(f => {
                  const diff = Date.now() - new Date(f.last_modified).getTime();
                  return diff > 24 * 60 * 60 * 1000;
                }) && (
                  <div style={{ fontSize:"0.78rem", color:"#d97706", background:"rgba(251,191,36,0.08)", padding:"8px 10px", borderRadius:"6px", border:"1px solid rgba(251,191,36,0.25)", marginBottom:"12px" }}>
                    ⚠️ No DBF files modified in the last 24 hours. RetailMan may not be writing to this folder — check if this is the correct PC or if RetailMan uses a network path.
                  </div>
                )}
              </>
            )}

            <div style={{ display:"flex", justifyContent:"flex-end", gap:"8px" }}>
              <button
                onClick={handleRetailmanDiagnose}
                disabled={diagnosing}
                style={{ padding:"0.5rem 1rem", background:"#f8f9fa", color:"#333", border:"1px solid #ddd", borderRadius:"6px", cursor: diagnosing ? "not-allowed" : "pointer", fontSize:"0.85rem" }}
              >
                {diagnosing ? "Running..." : "🔄 Re-run"}
              </button>
              <button
                onClick={() => setDiagnoseOpen(false)}
                style={{ padding:"0.5rem 1.25rem", background:"#007bff", color:"white", border:"none", borderRadius:"6px", cursor:"pointer", fontWeight:600, fontSize:"0.85rem" }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );

  return (
    <>
      {ReactDOM.createPortal(modals, document.body)}
      <aside className="sidebar">

        <div className="sidebar-scroll">
          <div className="sidebar-title">Menu</div>

          <button onClick={() => navigate("/dashboard")} className="sidebar-btn"><FaTachometerAlt style={{ marginRight:"8px" }} /> Dashboard</button>
          <button onClick={() => navigate("/PharmacyProfile")} className="sidebar-btn"><FaUserCircle style={{ marginRight:"8px" }} /> Profile</button>
          <button onClick={() => navigate("/POS")} className="sidebar-btn"><FaCashRegister style={{ marginRight:"8px" }} /> POS</button>

          {/* INVENTORY */}
          <div className="inventory-button-wrapper">
            <button onClick={handleInventoryClick} className={`sidebar-btn ${isStaff && !hasInventoryAccess ? "permission-disabled" : ""}`}>
              <FaBoxes style={{ marginRight:"8px" }} /> Manage Inventory
              {isStaff && !hasInventoryAccess && <FaLock style={{ marginLeft:"8px", fontSize:"0.8rem", color:"#ff6b6b" }} />}
            </button>
            {isStaff && (
              <div className="permission-status">
                {hasInventoryAccess ? <span className="permission-allowed">✓ Allowed</span> : <span className="permission-denied">✗ Restricted</span>}
              </div>
            )}
          </div>

          {isPharmacyOwner && <button onClick={() => navigate("/subscription")} className="sidebar-btn subscription-btn"><FaCreditCard style={{ marginRight:"8px" }} /> 💳 Subscription</button>}
          {isPharmacyOwner && <>
            <button onClick={() => navigate("/ManagePharmacyBranch")} className="sidebar-btn"><FaListUl style={{ marginRight:"8px" }} /> Manage Branches</button>
            <button onClick={() => navigate("/PharmacyBranches")} className="sidebar-btn"><FaListUl style={{ marginRight:"8px" }} /> View Branches</button>
          </>}
          {isPharmacyOwner && <button onClick={() => navigate("/StaffManagement")} className="sidebar-btn"><FaUsers style={{ marginRight:"8px" }} /> Staff Management</button>}

          {!isStaff && (
            <div className="analytics-menu">
              <button onClick={handleAnalyticsClick} className="sidebar-btn">
                <FaChartLine style={{ marginRight:"8px" }} /> Analytics {analyticsOpen ? <FaAngleUp /> : <FaAngleDown />}
              </button>
              {analyticsOpen && (
                <div className="submenu">
                  <button onClick={() => navigate("/DrugAnalytics")} className="sidebar-btn submenu-btn">Drug Analytics</button>
                  {isPharmacyOwner && <button onClick={() => navigate("/BranchAnalytics")} className="sidebar-btn submenu-btn">Branch Analytics</button>}
                </div>
              )}
            </div>
          )}

          {!isStaff && <button onClick={() => navigate("/SalesHistory")} className="sidebar-btn"><FaListUl style={{ marginRight:"8px" }} /> Sales History</button>}

          {(isPharmacyOwner || isBranchUser) && (
            <div className="export-inventory-wrapper">
              <button onClick={handleExportInventory} disabled={isExporting || (isPharmacyOwner && !ownerCanExport)} className={`sidebar-btn export-btn ${isPharmacyOwner && !ownerCanExport ? "subscription-locked" : ""}`}>
                <FaDownload style={{ marginRight:"8px" }} />
                {isExporting ? "Exporting..." : "📊 Export Inventory"}
                {isPharmacyOwner && !ownerCanExport && <FaLock style={{ marginLeft:"8px", fontSize:"0.8rem", color:"#ff9800" }} />}
              </button>
              <div className="subscription-status-badge">
                {isPharmacyOwner
                  ? ownerCanExport ? <span className="subscription-active">✓ {subscriptionStatus?.status === "TRIALING" ? "Trial Active" : "Subscription Active"}</span> : <span className="subscription-inactive">✗ Upgrade Required</span>
                  : <span className="subscription-active">✓ Branch Export</span>
                }
              </div>
            </div>
          )}

          {/* ── SETTINGS (DESKTOP ONLY) ── */}
          {IS_DESKTOP && (
            <div className="settings-menu">
              <button onClick={() => setSettingsOpen(o => !o)} className="sidebar-btn settings-toggle-btn">
                <FaCog style={{ marginRight:"8px", transition:"transform 0.3s ease", transform: settingsOpen ? "rotate(90deg)" : "rotate(0deg)" }} />
                Settings
                <span style={{ marginLeft:"auto" }}>{settingsOpen ? <FaAngleUp /> : <FaAngleDown />}</span>
              </button>

              {settingsOpen && (
                <div className="settings-panel">
                  <div className="settings-section-label">
                    <FaPlug style={{ marginRight:"6px", fontSize:"0.75rem" }} /> POS Connectors
                  </div>

                  {/* RetailMan row */}
                  <div style={{
                    padding:"12px", borderRadius:"10px",
                    background: retailman?.enabled ? "rgba(22,163,74,0.08)" : "rgba(148,163,184,0.06)",
                    border: retailman?.enabled ? "1px solid rgba(22,163,74,0.25)" : "1px solid rgba(148,163,184,0.18)",
                    transition:"all 0.2s ease",
                  }}>
                    {/* Header row: name + badges + toggle */}
                    <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:"12px" }}>
                      <div style={{ flex:1, minWidth:0 }}>

                        {/* Name + status badges */}
                        <div style={{ display:"flex", alignItems:"center", gap:"6px", marginBottom:"4px", flexWrap:"wrap" }}>
                          <span style={{ fontWeight:600, fontSize:"0.88rem", color:"var(--color-text-primary)" }}>RetailMan</span>
                          {retailman?.detected
                            ? <span style={{ fontSize:"0.68rem", fontWeight:600, padding:"2px 7px", borderRadius:"20px", background:"rgba(22,163,74,0.12)", color:"#15803d", border:"1px solid rgba(22,163,74,0.25)" }}>Detected</span>
                            : <span style={{ fontSize:"0.68rem", fontWeight:600, padding:"2px 7px", borderRadius:"20px", background:"rgba(239,68,68,0.1)", color:"#dc2626", border:"1px solid rgba(239,68,68,0.2)" }}>Not found</span>
                          }
                          {isRmanSyncing && (
                            <span style={{ fontSize:"0.68rem", fontWeight:600, padding:"2px 7px", borderRadius:"20px", background:"rgba(59,130,246,0.12)", color:"#1d4ed8", border:"1px solid rgba(59,130,246,0.25)" }}>
                              🔄 Syncing...
                            </span>
                          )}
                        </div>

                        {/* Status dot */}
                        <div style={{ display:"flex", alignItems:"center", gap:"5px", marginBottom:"4px" }}>
                          <span style={{ display:"inline-block", width:"6px", height:"6px", borderRadius:"50%", flexShrink:0, background: retailman?.enabled ? "#16a34a" : "#94a3b8", boxShadow: retailman?.enabled ? "0 0 0 2px rgba(22,163,74,0.25)" : "none" }} />
                          <span style={{ fontSize:"0.78rem", color: retailman?.enabled ? "#15803d" : "#94a3b8" }}>
                            {retailman?.enabled ? `Active · Last: ${formatLastSynced(rman?.last_synced_at || retailman?.last_synced)}` : "Sync off"}
                          </span>
                        </div>

                        {/* Last result */}
                        {retailman?.enabled && rman?.last_result && (
                          <div style={{ fontSize:"0.72rem", color:"#6b7280", marginBottom:"3px" }}>
                            +{rman.last_result.added ?? 0} added · ✏️ {rman.last_result.updated ?? 0} updated · ⏭ {rman.last_result.skipped ?? 0} skipped
                            {rman.last_result.failed > 0 && <span style={{ color:"#dc2626" }}> · ❌ {rman.last_result.failed} failed</span>}
                          </div>
                        )}

                        {/* Folder path */}
                        {retailman?.folder && (
                          <div title={retailman.folder} style={{ fontSize:"0.72rem", color:"#6b7280", fontFamily:"monospace", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", maxWidth:"160px" }}>
                            {retailman.folder.length > 26 ? "..." + retailman.folder.slice(-23) : retailman.folder}
                          </div>
                        )}
                      </div>

                      {/* Toggle switch */}
                      <button
                        onClick={handleToggleRetailman}
                        disabled={togglingRetailman || !connectorSettings}
                        aria-label={`RetailMan sync ${retailman?.enabled ? "on" : "off"}`}
                        style={{
                          position:"relative", display:"inline-flex", alignItems:"center",
                          width:"52px", height:"28px", borderRadius:"14px", border:"none", padding:0,
                          cursor: togglingRetailman || !connectorSettings ? "not-allowed" : "pointer",
                          background: retailman?.enabled ? "#16a34a" : "#94a3b8",
                          transition:"background 0.25s ease", opacity: togglingRetailman ? 0.6 : 1,
                          flexShrink:0, outline:"none", boxShadow:"inset 0 1px 3px rgba(0,0,0,0.2)", marginTop:"2px",
                        }}
                      >
                        <span style={{
                          position:"absolute", top:"3px",
                          left: retailman?.enabled ? "27px" : "3px",
                          width:"22px", height:"22px", borderRadius:"50%", background:"#fff",
                          transition:"left 0.25s ease", boxShadow:"0 1px 4px rgba(0,0,0,0.25)",
                        }} />
                      </button>
                    </div>

                    {/* ── RetailMan real-time progress bar (shows during sync) ── */}
                    {retailman?.enabled && isRmanSyncing && (
                      <div style={{ marginTop:"10px" }}>
                        <div style={{ display:"flex", justifyContent:"space-between", fontSize:"0.70rem", color:"#6b7280", marginBottom:"4px" }}>
                          <span>Syncing RetailMan stock...</span>
                          <span>
                            {rmanTotal > 0
                              ? `${rmanProcessed.toLocaleString()} / ${rmanTotal.toLocaleString()} (${rmanPercent ?? 0}%)`
                              : `${rmanProcessed.toLocaleString()} items`
                            }
                          </span>
                        </div>
                        <div style={{ height:"5px", borderRadius:"3px", background:"rgba(59,130,246,0.15)", overflow:"hidden" }}>
                          {rmanPercent != null ? (
                            <div style={{ height:"100%", width:`${rmanPercent}%`, borderRadius:"3px", background:"linear-gradient(90deg,#3b82f6,#60a5fa)", transition:"width 0.4s ease" }} />
                          ) : (
                            <div style={{ height:"100%", width:"100%", borderRadius:"3px", background:"linear-gradient(90deg,#3b82f6,#60a5fa,#3b82f6)", backgroundSize:"200% 100%", animation:"shimmer 1.5s infinite" }} />
                          )}
                        </div>
                        {rmanTotal > 0 && (
                          <div style={{ display:"flex", gap:"8px", flexWrap:"wrap", marginTop:"5px", fontSize:"0.68rem" }}>
                            {rmanAdded   > 0 && <span style={{ color:"#16a34a" }}>+{rmanAdded} added</span>}
                            {rmanUpdated > 0 && <span style={{ color:"#3b82f6" }}>✏️ {rmanUpdated} updated</span>}
                            {rmanSkipped > 0 && <span style={{ color:"#9ca3af" }}>⏭ {rmanSkipped} skipped</span>}
                            {rmanFailed  > 0 && <span style={{ color:"#dc2626" }}>❌ {rmanFailed} failed</span>}
                          </div>
                        )}
                        {rmanSyncProgress.started_at && (
                          <div style={{ fontSize:"0.68rem", color:"#9ca3af", marginTop:"3px" }}>
                            Started {new Date(rmanSyncProgress.started_at).toLocaleTimeString()}
                          </div>
                        )}
                      </div>
                    )}

                    {/* ── Last sync complete summary ── */}
                    {retailman?.enabled && !isRmanSyncing && rmanSyncProgress.status === "complete" && rmanSyncProgress.total > 0 && (
                      <div style={{ marginTop:"8px", fontSize:"0.70rem", color:"#16a34a", display:"flex", alignItems:"center", gap:"4px", flexWrap:"wrap" }}>
                        <FaCheckCircle style={{ fontSize:"0.65rem" }} />
                        <span>Last sync: {rmanSyncProgress.total?.toLocaleString()} items</span>
                        {rmanSyncProgress.added   > 0 && <span style={{ color:"#16a34a" }}>· +{rmanSyncProgress.added} added</span>}
                        {rmanSyncProgress.updated > 0 && <span style={{ color:"#3b82f6" }}>· ✏️ {rmanSyncProgress.updated} updated</span>}
                        {rmanSyncProgress.failed  > 0 && <span style={{ color:"#dc2626" }}>· ❌ {rmanSyncProgress.failed} failed</span>}
                        {rmanSyncProgress.completed_at && <span style={{ color:"#9ca3af" }}>· {formatLastSynced(rmanSyncProgress.completed_at)}</span>}
                      </div>
                    )}

                    {/* ── Action buttons ── */}
                    <div style={{ display:"flex", flexDirection:"column", gap:"6px", marginTop:"10px", width:"100%" }}>
                      {retailman?.enabled && (
                        <button
                          onClick={handleRetailmanSyncNow}
                          disabled={isRmanSyncing || retailmanSyncing}
                          style={{
                            width:"100%", padding:"7px 8px", borderRadius:"6px", border:"none",
                            background: isRmanSyncing || retailmanSyncing ? "#94a3b8" : "#3b82f6",
                            color:"#fff", fontSize:"0.76rem", fontWeight:600,
                            cursor: isRmanSyncing || retailmanSyncing ? "not-allowed" : "pointer",
                            display:"flex", alignItems:"center", justifyContent:"center", gap:"5px",
                            transition:"background 0.2s ease",
                          }}
                        >
                          <FaBolt style={{ fontSize:"0.65rem", flexShrink:0 }} />
                          {isRmanSyncing || retailmanSyncing ? "Syncing..." : "Sync Now"}
                        </button>
                      )}

                      {retailman?.detected && (
                        <button
                          onClick={handleRetailmanDiagnose}
                          disabled={diagnosing}
                          title="Check RetailMan folder, files, disk space"
                          style={{
                            width:"100%", padding:"6px 8px", borderRadius:"6px",
                            border:"1px solid rgba(148,163,184,0.25)",
                            background:"rgba(148,163,184,0.08)", color:"#94a3b8",
                            fontSize:"0.74rem", fontWeight:500,
                            cursor: diagnosing ? "not-allowed" : "pointer",
                            display:"flex", alignItems:"center", justifyContent:"center", gap:"5px",
                            transition:"all 0.2s ease",
                          }}
                        >
                          <FaSearch style={{ fontSize:"0.65rem", flexShrink:0 }} />
                          {diagnosing ? "Running diagnostics..." : "🔍 Diagnose"}
                        </button>
                      )}
                    </div>

                    {/* Activity log — last 3 entries */}
                    {retailman?.enabled && rman?.activity_log?.length > 0 && (
                      <div style={{ marginTop:"8px", borderTop:"1px solid rgba(148,163,184,0.15)", paddingTop:"6px" }}>
                        {rman.activity_log.slice(0, 3).map((entry, i) => (
                          <div key={i} style={{ fontSize:"0.70rem", lineHeight:1.4, marginBottom:"2px", color: entry.level === "error" ? "#dc2626" : entry.level === "warning" ? "#d97706" : "#6b7280" }}>
                            <span style={{ color:"#9ca3af", marginRight:"4px" }}>
                              {new Date(entry.timestamp).toLocaleTimeString([], { hour:"2-digit", minute:"2-digit" })}
                            </span>
                            {entry.message}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <p style={{ fontSize:"0.75rem", color:"#6b7280", margin:"8px 0 0", lineHeight:1.5 }}>
                    {retailman?.enabled
                      ? "Inventory is read-only while RetailMan sync is active. Manage stock in RetailMan."
                      : "When enabled, MyApoti reads your RetailMan stock every 10 minutes automatically."}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── SYNC PANEL (DESKTOP ONLY) ── */}
        {IS_DESKTOP && (
          <div className="sync-panel">
            <div className="sync-divider" />

            <div className="sync-status-row">
              <div className="sync-online-indicator">
                <FaWifi style={{ color: syncStatus === null ? "#94a3b8" : syncStatus?.online ? "#22c55e" : "#ef4444", fontSize:"0.85rem", marginRight:"6px" }} />
                <span style={{ fontSize:"0.78rem", fontWeight:600, color: syncStatus === null ? "#94a3b8" : syncStatus?.online ? "#22c55e" : "#ef4444" }}>
                  {syncStatus === null ? "Checking..." : syncStatus?.online ? "Online" : "Offline"}
                </span>
              </div>
              {syncStatus?.failed_count > 0 && (
                <div className="sync-failed-badge">
                  <FaExclamationTriangle style={{ fontSize:"0.75rem", marginRight:"4px" }} />
                  {syncStatus.failed_count} failed
                </div>
              )}
              {syncStatus?.pending_count > 0 && (
                <div style={{ fontSize:"0.72rem", color:"#d97706", fontWeight:600, display:"flex", alignItems:"center", gap:"3px" }}>
                  <FaArrowUp style={{ fontSize:"0.65rem" }} />
                  {syncStatus.pending_count} pending
                </div>
              )}
            </div>

            {syncStatus?.online && syncStatus?.pending_count === 0 && syncStatus?.failed_count === 0 && (
              <div style={{ fontSize:"0.75rem", color:"#16a34a", display:"flex", alignItems:"center", gap:"5px", marginBottom:"6px" }}>
                <FaCheckCircle style={{ fontSize:"0.7rem" }} /> All changes synced to cloud
              </div>
            )}

            {syncStatus?.next_auto_sync_in_seconds != null && (
              <div style={{ fontSize:"0.72rem", color:"#9ca3af", display:"flex", alignItems:"center", gap:"4px", marginBottom:"6px" }}>
                <FaClock style={{ fontSize:"0.65rem" }} />
                Next auto-sync in {formatNextSync(syncStatus.next_auto_sync_in_seconds)}
              </div>
            )}

            {isPulling && (
              <div style={{ marginBottom:"8px" }}>
                <div style={{ display:"flex", justifyContent:"space-between", fontSize:"0.72rem", color:"#6b7280", marginBottom:"4px" }}>
                  <span>⬇ Pulling from cloud...</span>
                  <span>
                    {pullTotal > 0
                      ? `${pullPulled.toLocaleString()} / ${pullTotal.toLocaleString()} (${pullPercent}%)`
                      : `${pullPulled.toLocaleString()} drugs · page ${pullProgress?.page || 1}`
                    }
                  </span>
                </div>
                <div style={{ height:"5px", borderRadius:"3px", background:"rgba(59,130,246,0.15)", overflow:"hidden" }}>
                  {pullPercent != null ? (
                    <div style={{ height:"100%", width:`${pullPercent}%`, borderRadius:"3px", background:"linear-gradient(90deg,#3b82f6,#60a5fa)", transition:"width 0.5s ease" }} />
                  ) : (
                    <div style={{ height:"100%", width:"100%", background:"linear-gradient(90deg,#3b82f6,#60a5fa,#3b82f6)", backgroundSize:"200% 100%", animation:"shimmer 1.5s infinite" }} />
                  )}
                </div>
                {pullProgress?.started_at && (
                  <div style={{ fontSize:"0.68rem", color:"#9ca3af", marginTop:"3px" }}>
                    Started {new Date(pullProgress.started_at).toLocaleTimeString()}
                  </div>
                )}
              </div>
            )}

            {!isPulling && pullProgress?.status === "complete" && pullProgress?.pulled > 0 && (
              <div style={{ fontSize:"0.72rem", color:"#16a34a", display:"flex", alignItems:"center", gap:"4px", marginBottom:"6px" }}>
                <FaCheckCircle style={{ fontSize:"0.65rem" }} />
                Pulled {pullProgress.pulled.toLocaleString()} drugs
                {pullProgress.completed_at && ` · ${formatLastSynced(pullProgress.completed_at)}`}
              </div>
            )}

            <button
              onClick={() => setProgressOpen(o => !o)}
              style={{ width:"100%", background:"none", border:"none", padding:"4px 0", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"space-between", fontSize:"0.75rem", color:"#6b7280", marginBottom:"4px" }}
            >
              <span style={{ display:"flex", alignItems:"center", gap:"5px" }}>
                <FaDatabase style={{ fontSize:"0.65rem" }} /> Sync details
              </span>
              {progressOpen ? <FaAngleUp style={{ fontSize:"0.65rem" }} /> : <FaAngleDown style={{ fontSize:"0.65rem" }} />}
            </button>

            {progressOpen && (
              <div style={{ background:"rgba(148,163,184,0.06)", borderRadius:"8px", padding:"10px", marginBottom:"8px", border:"1px solid rgba(148,163,184,0.15)", fontSize:"0.75rem", color:"#6b7280" }}>
                {!syncProgress ? (
                  <div style={{ color:"#9ca3af", textAlign:"center" }}>Loading...</div>
                ) : (
                  <>
                    <div style={{ display:"flex", justifyContent:"space-between", marginBottom:"6px" }}>
                      <span>💊 Drugs in local DB</span>
                      <span style={{ fontWeight:600, color:"var(--color-text-primary)" }}>{(syncProgress.inventory_count || 0).toLocaleString()}</span>
                    </div>
                    <div style={{ display:"flex", justifyContent:"space-between", marginBottom:"6px" }}>
                      <span><FaArrowDown style={{ fontSize:"0.6rem", color:"#3b82f6", marginRight:"3px" }} />Last pull</span>
                      <span style={{ fontWeight:600, color: syncProgress.last_pull_at ? "#3b82f6" : "#ef4444" }}>
                        {syncProgress.last_pull_at ? formatLastSynced(syncProgress.last_pull_at) : "Never — full pull pending"}
                      </span>
                    </div>
                    <div style={{ display:"flex", justifyContent:"space-between", marginBottom:"6px" }}>
                      <span><FaArrowUp style={{ fontSize:"0.6rem", color:"#16a34a", marginRight:"3px" }} />Push queue</span>
                      <span style={{ fontWeight:600 }}>
                        <span style={{ color: syncProgress.queue?.pending > 0 ? "#d97706" : "#16a34a" }}>{syncProgress.queue?.pending ?? 0} pending</span>
                        {syncProgress.queue?.failed > 0 && <span style={{ color:"#dc2626", marginLeft:"6px" }}>{syncProgress.queue.failed} failed</span>}
                      </span>
                    </div>
                    {syncProgress.last_sync && Object.keys(syncProgress.last_sync).length > 0 && (
                      <>
                        <div style={{ borderTop:"1px solid rgba(148,163,184,0.15)", paddingTop:"6px", marginTop:"4px", marginBottom:"4px", fontSize:"0.70rem", color:"#9ca3af" }}>Last sync result</div>
                        <div style={{ display:"flex", gap:"8px", flexWrap:"wrap" }}>
                          {syncProgress.last_sync.pulled != null && <span style={{ color:"#16a34a" }}>⬇ {syncProgress.last_sync.pulled} pulled</span>}
                          {syncProgress.last_sync.synced != null && <span style={{ color:"#3b82f6" }}>⬆ {syncProgress.last_sync.synced} pushed</span>}
                          {syncProgress.last_sync.failed > 0 && <span style={{ color:"#dc2626" }}>❌ {syncProgress.last_sync.failed} failed</span>}
                          {syncProgress.last_sync.timestamp && <span style={{ color:"#9ca3af", marginLeft:"auto" }}>{formatLastSynced(syncProgress.last_sync.timestamp)}</span>}
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
            )}

            {lastSyncTime && <div className="sync-last-time">Manually synced: {formatSyncTime(lastSyncTime)}</div>}

            <button onClick={handleManualSync} disabled={isSyncing} className={`sidebar-btn sync-now-btn ${isSyncing ? "syncing" : ""}`}>
              <FaSync style={{ marginRight:"8px", animation: isSyncing ? "spin 1s linear infinite" : "none" }} />
              {isSyncing ? "Syncing..." : "🔄 Sync Now"}
            </button>

            <p className="sync-description">Updates inventory, sales, staff, subscription and notifications from the cloud.</p>

            {lastSyncMessage && (
              <div className={`sync-message sync-message--${lastSyncMessage.type}`}>
                {lastSyncMessage.type === "success" && "✅ "}
                {lastSyncMessage.type === "warning" && "⚠️ "}
                {lastSyncMessage.type === "error" && "❌ "}
                {lastSyncMessage.text}
              </div>
            )}

            {syncStatus?.failed_count > 0 && (
              <button onClick={() => navigate("/Settings")} className="sync-settings-link">View failed syncs in Settings →</button>
            )}
          </div>
        )}
      </aside>
    </>
  );
};

export default Sidebar;